import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import api from "@/lib/api";
import { formatINR } from "@/lib/site";

const EMPTY_PKG = {
  title: "", slug: "", category: "domestic", destination: "", type: [], duration_days: 5, nights: 4,
  price: 0, original_price: 0, rating: 4.5, reviews: 0, featured: false, image: "",
  gallery: [], highlights: [], route: [], itinerary: [], inclusions: [], exclusions: [],
};

const STATUS_COLORS = { new: "bg-sun/15 text-sun", contacted: "bg-blue-500/15 text-blue-400", closed: "bg-green-500/15 text-green-400" };

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PKG);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setChecking(false); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => { setEnquiries(r.data.enquiries); setContacts(r.data.contacts); }).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const openNew = () => { setForm(EMPTY_PKG); setEditing({ isNew: true }); };
  const openEdit = (pkg) => { setForm({ ...EMPTY_PKG, ...pkg }); setEditing(pkg); };

  const savePackage = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      duration_days: Number(form.duration_days), nights: Number(form.nights),
      price: Number(form.price), original_price: Number(form.original_price) || null,
      rating: Number(form.rating), reviews: Number(form.reviews),
      type: typeof form.type === "string" ? form.type.split(",").map((s) => s.trim()).filter(Boolean) : form.type,
      highlights: typeof form.highlights === "string" ? form.highlights.split("\n").filter(Boolean) : form.highlights,
      itinerary: typeof form.itinerary === "string"
        ? form.itinerary.split("\n").filter(Boolean).map((line, i) => {
            const [t, d] = line.split("|").map((s) => s.trim());
            return { day: i + 1, title: t || `Day ${i + 1}`, desc: d || "" };
          })
        : form.itinerary,
      inclusions: typeof form.inclusions === "string" ? form.inclusions.split("\n").filter(Boolean) : form.inclusions,
      exclusions: typeof form.exclusions === "string" ? form.exclusions.split("\n").filter(Boolean) : form.exclusions,
    };
    try {
      if (editing.isNew) await api.post("/admin/packages", payload);
      else await api.put(`/admin/packages/${editing.slug}`, payload);
      toast.success("Package saved");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const deletePackage = async (slug) => {
    if (!window.confirm(`Delete package "${slug}"?`)) return;
    try {
      await api.delete(`/admin/packages/${slug}`);
      toast.success("Package deleted");
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  if (checking) return <div className="min-h-screen bg-ink flex items-center justify-center text-bone/40">Loading...</div>;

  const input = "w-full bg-white border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-sun";
  const label = "text-xs font-semibold text-ink/50 uppercase tracking-wider mb-1 block";

  const EnquiryRow = ({ e, isContact }) => (
    <tr className="border-b border-border hover:bg-sand/30 transition-colors" data-testid={`enquiry-row-${e.id}`}>
      <td className="px-4 py-3">
        <p className="font-semibold text-sm">{e.name}</p>
        <p className="text-xs text-ink/50">{e.phone}{e.email ? ` · ${e.email}` : ""}</p>
      </td>
      <td className="px-4 py-3 text-sm">{isContact ? (e.subject || "Contact form") : (e.package_title || "General")}</td>
      <td className="px-4 py-3 text-xs text-ink/60 hidden md:table-cell">
        {isContact ? e.message : `${e.travelers || "-"} travelers${e.travel_date ? ` · ${e.travel_date}` : ""}${e.message ? ` — ${e.message}` : ""}`}
      </td>
      <td className="px-4 py-3 text-xs text-ink/50 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString("en-IN")}</td>
      <td className="px-4 py-3">
        <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id}`}
          className={`text-xs font-semibold rounded-full px-3 py-1.5 cursor-pointer focus:outline-none ${STATUS_COLORS[e.status] || STATUS_COLORS.new}`}>
          <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
        </select>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <div className="bg-ink text-bone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="font-display text-xl font-black">click<span className="text-sun">2</span>trip <span className="text-xs font-normal text-bone/40 ml-1">admin</span></p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="text-xs text-bone/50 hover:text-sun flex items-center gap-1"><ArrowLeft size={13} /> View site</Link>
            <span className="text-xs text-bone/40 hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-xs bg-bone/10 hover:bg-sun px-3.5 py-2 rounded-full transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
            {[["New enquiries", stats.new_enquiries], ["Total enquiries", stats.enquiries], ["Contact msgs", stats.contacts], ["Packages", stats.packages], ["Subscribers", stats.subscribers]].map(([l, v]) => (
              <div key={l} className="bg-white border border-border rounded-xl p-4">
                <p className="font-display text-3xl font-black">{v}</p>
                <p className="text-xs text-ink/50 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            {["enquiries", "packages"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-ink text-bone" : "bg-sand/60 text-ink/60 hover:bg-sand"}`}>
                {t}
              </button>
            ))}
          </div>
          {tab === "packages" && (
            <button onClick={openNew} data-testid="admin-add-package-btn"
              className="flex items-center gap-1.5 bg-sun text-bone text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#e85a28] transition-colors">
              <Plus size={15} /> Add Package
            </button>
          )}
        </div>

        {tab === "enquiries" && (
          <div className="bg-white border border-border rounded-2xl overflow-x-auto" data-testid="admin-enquiries-table">
            <table className="w-full text-left min-w-[720px]">
              <thead className="bg-sand/40 text-xs uppercase tracking-wider text-ink/50">
                <tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Package / Subject</th><th className="px-4 py-3 hidden md:table-cell">Details</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {enquiries.map((e) => <EnquiryRow key={e.id} e={e} />)}
                {contacts.map((c) => <EnquiryRow key={c.id} e={c} isContact />)}
                {enquiries.length + contacts.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-ink/40 text-sm" data-testid="no-enquiries-message">No enquiries yet — they'll appear here as they arrive.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "packages" && (
          <div className="bg-white border border-border rounded-2xl overflow-x-auto" data-testid="admin-packages-table">
            <table className="w-full text-left min-w-[720px]">
              <thead className="bg-sand/40 text-xs uppercase tracking-wider text-ink/50">
                <tr><th className="px-4 py-3">Package</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Duration</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Featured</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {packages.map((p) => (
                  <tr key={p.slug} className="border-b border-border hover:bg-sand/30" data-testid={`admin-package-row-${p.slug}`}>
                    <td className="px-4 py-3"><p className="font-semibold text-sm">{p.title}</p><p className="text-xs text-ink/50">{p.slug}</p></td>
                    <td className="px-4 py-3 text-sm capitalize">{p.category}</td>
                    <td className="px-4 py-3 text-sm">{p.duration_days}D/{p.nights}N</td>
                    <td className="px-4 py-3 text-sm font-semibold">{formatINR(p.price)}</td>
                    <td className="px-4 py-3 text-sm">{p.featured ? "Yes" : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(p)} data-testid={`edit-package-${p.slug}`} className="p-2 text-ink/50 hover:text-sun transition-colors"><Pencil size={15} /></button>
                        <button onClick={() => deletePackage(p.slug)} data-testid={`delete-package-${p.slug}`} className="p-2 text-ink/50 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-2xl bg-white rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="package-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">{editing?.isNew ? "Add Package" : `Edit: ${editing?.title}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={savePackage} className="grid grid-cols-2 gap-4 mt-2" data-testid="package-form">
            <div className="col-span-2 sm:col-span-1"><span className={label}>Title *</span><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} data-testid="pkg-title-input" /></div>
            <div className="col-span-2 sm:col-span-1"><span className={label}>Slug *</span><input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={input} data-testid="pkg-slug-input" disabled={!editing?.isNew} /></div>
            <div><span className={label}>Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} data-testid="pkg-category-select">
                <option value="domestic">Domestic</option><option value="international">International</option>
              </select></div>
            <div><span className={label}>Destination *</span><input required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className={input} data-testid="pkg-destination-input" /></div>
            <div><span className={label}>Types (comma separated)</span><input value={Array.isArray(form.type) ? form.type.join(", ") : form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={input} placeholder="beach, honeymoon" data-testid="pkg-type-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={label}>Days</span><input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className={input} data-testid="pkg-days-input" /></div>
              <div><span className={label}>Nights</span><input type="number" value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} className={input} data-testid="pkg-nights-input" /></div>
            </div>
            <div><span className={label}>Price (₹) *</span><input required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} data-testid="pkg-price-input" /></div>
            <div><span className={label}>Original price (₹)</span><input type="number" value={form.original_price || ""} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className={input} data-testid="pkg-original-price-input" /></div>
            <div className="col-span-2"><span className={label}>Main image URL *</span><input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={input} data-testid="pkg-image-input" /></div>
            <div className="col-span-2"><span className={label}>Highlights (one per line)</span><textarea rows={3} value={Array.isArray(form.highlights) ? form.highlights.join("\n") : form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} className={input} data-testid="pkg-highlights-input" /></div>
            <div className="col-span-2"><span className={label}>Itinerary (one per line: Title | Description)</span><textarea rows={4} value={Array.isArray(form.itinerary) ? form.itinerary.map((d) => `${d.title} | ${d.desc}`).join("\n") : form.itinerary} onChange={(e) => setForm({ ...form, itinerary: e.target.value })} className={input} data-testid="pkg-itinerary-input" /></div>
            <div><span className={label}>Inclusions (one per line)</span><textarea rows={3} value={Array.isArray(form.inclusions) ? form.inclusions.join("\n") : form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} className={input} data-testid="pkg-inclusions-input" /></div>
            <div><span className={label}>Exclusions (one per line)</span><textarea rows={3} value={Array.isArray(form.exclusions) ? form.exclusions.join("\n") : form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} className={input} data-testid="pkg-exclusions-input" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-sun" data-testid="pkg-featured-checkbox" />
              Show in Featured section on homepage
            </label>
            <button type="submit" data-testid="pkg-save-btn"
              className="col-span-2 bg-sun text-bone font-semibold py-3 rounded-full hover:bg-[#e85a28] transition-colors">
              Save Package
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
